import { useCallback, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';

import { MartyContext } from 'utils/context';
import { evRecommendationClick } from 'events/recommendations';
import { track } from 'apis/amethyst';
import VipOnlyLogo from 'components/icons/vipDashboard/VipOnlyLogo';
import MelodyCardCategory from 'components/common/melodyCard/MelodyCardCategory';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import Carousel from 'components/common/Carousel/Carousel';
import { createNonProductModuleViewEvent, mapBrands, MODULE_EVENT_NAME } from 'actions/pixelServerUtil';
import { firePixelServer } from 'actions/pixelServer';

import css from 'styles/components/landing/melodyPersonalizedBrand.scss';

const widgetType = 'BRAND_RECOMMENDATION_WIDGET';
const recommendationSource = 'ZAPPOS_DATA_SCIENCE';
const recommendationType = 'BRAND_RECOMMENDATION';

export const MelodyPersonalizedBrand = ({
  firstName,
  slotName,
  slotDetails,
  shouldLazyLoad,
  slideWidths,
  onComponentClick,
  isOnSearchPage = false
}) => {
  const dispatch = useDispatch();
  const { brands } = slotDetails;

  const handleClick = useCallback(
    (e, index, id) => {
      onComponentClick(e);
      track(() => [
        evRecommendationClick,
        {
          index,
          recommendationType,
          recommendationValue: id,
          recommendationSource,
          widgetType
        }
      ]);
    },
    [onComponentClick]
  );

  const makeHeadingText = useCallback(
    isTrending => {
      if (isTrending) {
        return 'Trending Brands';
      }
      return firstName ? `${firstName}, You Might Like These Brands` : 'Brands For You';
    },
    [firstName]
  );

  const makeBrandPromo = useCallback(
    () => (
      <p className={css.brandPromo}>
        <VipOnlyLogo />
        Bonus Points
      </p>
    ),
    []
  );

  useEffect(() => {
    if (!brands?.length) {
      return;
    }

    dispatchPixelEvent();
  }, [brands?.length]);

  const dispatchPixelEvent = () => {
    if (!isOnSearchPage) {
      return;
    }
    const recoSource = brands[0].recommendationSource;
    const isTrending = recoSource && recoSource === 'TRENDING_BRANDS';

    const moduleViewEvent = createNonProductModuleViewEvent({
      moduleName: makeHeadingText(isTrending) ?? '',
      items: mapBrands({ brands }),
      type: 'brand'
    });
    dispatch(firePixelServer(MODULE_EVENT_NAME, moduleViewEvent));
  };

  return (
    <MartyContext.Consumer>
      {({ marketplace }) => {
        const { desktopBaseUrl, hasRewardsTransparency } = marketplace;

        if (brands && brands.length) {
          const recoSource = brands[0].recommendationSource;
          const isTrending = recoSource && recoSource === 'TRENDING_BRANDS';

          return (
            <div className={css.carousel} data-slot-id={slotName}>
              <Carousel
                forwardButtonOnClick={dispatchPixelEvent}
                slideWidths={slideWidths}
                slides={brands.map((brand, index) => {
                  const { headerImageUrl, name, cleanName, id, brandUrl, brandPromo } = brand;
                  const link = brandUrl || `/search/null/filter/brandNameFacet/${encodeURIComponent(name)}`;
                  const cardData = {
                    image: `${desktopBaseUrl}${headerImageUrl}`,
                    name,
                    alt: name,
                    gae: cleanName,
                    link
                  };

                  return (
                    <MelodyCardCategory
                      cardData={cardData}
                      eventLabel="melodyPersonalizedBrand"
                      melodyCardTestId="melodyPersonalizedBrand"
                      onComponentClick={e => {
                        handleClick(e, index, id);
                      }}
                      shouldLazyLoad={shouldLazyLoad}
                      key={id}
                    >
                      {brandPromo && hasRewardsTransparency && makeBrandPromo()}
                    </MelodyCardCategory>
                  );
                })}
                header={{ title: makeHeadingText(isTrending) }}
              />
            </div>
          );
        }
        return false;
      }}
    </MartyContext.Consumer>
  );
};

const mapStateToProps = state => ({
  firstName: state.holmes.firstName
});

const ConnectedMelodyPersonalizedBrand = connect(mapStateToProps, {})(MelodyPersonalizedBrand);
export default withErrorBoundary('MelodyPersonalizedBrand', ConnectedMelodyPersonalizedBrand);
